import React from "react";
import { useQuery } from "@apollo/client";
import Header from "@src/components/Header";
import Floater from "@src/components/Floater";
import Footer from "@src/components/Footer";
import {
  Event_PreferencesQuery,
  Event_PreferencesQueryVariables,
  EventBySlugQuery,
  EventPartsQuery,
  EventVendorQuery,
  GetAlbumDetailsQuery,
  GetBackStoryQuery,
  GetEventRecommendationListsQuery,
  GetEventStreamersQuery,
  GetFaQsByEventQuery,
  GetHeroImageQuery,
  GetLiveScheduleQuery,
  GetWeddingPartyQuery,
  SignupByInviteLinkMutation,
} from "src/@types/api";
import { GetEventPreferences } from "@src/graphql/queries";
import Hero from "@src/components/Hero";
import Quotes from "@src/components/Quotes";
import Backstory from "@src/components/Backstory";
import MarqueeComp from "@src/components/Marquee";
import Masonry from "@src/components/Masonry";
import Vendors from "@src/components/Vendors";
import Parts from "@src/components/Parts";
import ClientOnly from "@src/components/ClientOnly";
import { useEventParts } from "@src/hooks/useEventParts";
import { useHome } from "@src/hooks/useHome";
import WeddingParty from "../WeddingParty";
import FAQ from "../FAQ";
import Recommendations from "../Recommendations";

export type IHome = {
  slug: string;
  eventBySlugData?: EventBySlugQuery;
  eventId: number;
  eventPartsData: EventPartsQuery;
  backStoryData: GetBackStoryQuery;
  eventVendorData: EventVendorQuery;
  albumDetailsData: GetAlbumDetailsQuery;
  heroImageData: GetHeroImageQuery;
  hasWallet: boolean;
  getEventStreamersData: GetEventStreamersQuery;
  liveSchedules: GetLiveScheduleQuery;
  eventUuid?: string;
  weddingPartyData: GetWeddingPartyQuery;
  faqData?: GetFaQsByEventQuery;
  recommendationData?: GetEventRecommendationListsQuery;
  inviteResult?: SignupByInviteLinkMutation | null;
};

const Home: React.FC<IHome> = ({
  slug,
  eventBySlugData,
  eventId,
  eventPartsData,
  backStoryData,
  eventVendorData,
  albumDetailsData,
  heroImageData,
  hasWallet,
  getEventStreamersData,
  liveSchedules,
  eventUuid,
  weddingPartyData,
  faqData,
  recommendationData,
  inviteResult: initialInviteResult,
}) => {
  const { accessToken, inviteResult, isInvitedToEvent, getAsoebiData } =
    useHome({
      slug,
      eventBySlugData,
      eventId,
      eventPartsData,
      backStoryData,
      eventVendorData,
      albumDetailsData,
      heroImageData,
      hasWallet,
      getEventStreamersData,
      liveSchedules,
      eventUuid,
      weddingPartyData,
      faqData,
      recommendationData,
      inviteResult: initialInviteResult,
    });

  const { data: eventPreferencesData } = useQuery<
    Event_PreferencesQuery,
    Event_PreferencesQueryVariables
  >(GetEventPreferences, {
    variables: { eventId: eventUuid as string },
    skip: !eventUuid,
  });

  const shouldShowFaq = eventPreferencesData?.event_preferences?.faq ?? false;
  const shouldShowRecommendations =
    eventPreferencesData?.event_preferences?.recommendations ?? false;
  const { parts } = useEventParts({
    eventPartsData,
    liveSchedules,
    eventTimezone: eventBySlugData?.eventBySlug?.eventTimezone,
    isInvitedToEvent,
  });

  return (
    <>
      <Header eventId={eventId} slugData={eventBySlugData} />
      <ClientOnly>
        <Floater
          accessToken={accessToken}
          eventId={eventId}
          eventUuid={eventUuid}
          slugData={eventBySlugData}
          hasWallet={hasWallet}
          getEventStreamersData={getEventStreamersData}
          liveSchedules={liveSchedules}
          access_control={
            eventBySlugData?.eventBySlug?.event_preference?.access_control ??
            false
          }
          qr_code={
            initialInviteResult?.signupByInviteLink?.invitation?.qr_code ?? ""
          }
          getAsoebiData={getAsoebiData}
          admits={
            initialInviteResult?.signupByInviteLink?.invitation?.admits ?? 1
          }
          invitation={initialInviteResult?.signupByInviteLink?.invitation}
          isInvitedToEvent={isInvitedToEvent}
        />
      </ClientOnly>
      <Hero
        slug={slug}
        heroImageData={heroImageData}
        eventId={eventId}
        slugData={eventBySlugData}
        eventUuid={eventUuid}
      />
      <Parts
        liveSchedules={liveSchedules}
        eventId={eventId}
        parts={parts}
        eventPartsData={eventPartsData}
        eventTimezone={eventBySlugData?.eventBySlug?.eventTimezone}
        isInvitedToEvent={isInvitedToEvent}
      />

      {shouldShowFaq && faqData && <FAQ eventId={eventId} faqData={faqData} />}

      <Quotes />
      <Backstory eventId={eventId} backStoryData={backStoryData} />

      <WeddingParty eventId={eventId} weddingPartyData={weddingPartyData} />

      <ClientOnly>
        <MarqueeComp eventId={eventId} slugData={eventBySlugData} />
      </ClientOnly>

      <Masonry
        eventSlug={slug}
        eventId={eventId}
        albumDetailsData={albumDetailsData}
      />
      <Vendors eventId={eventId} eventVendorData={eventVendorData} />
      {shouldShowRecommendations &&
        recommendationData &&
        (recommendationData.getEventRecommendationLists?.length ?? 0) > 0 && (
          <Recommendations
            eventId={eventId}
            recommendationData={recommendationData}
          />
        )}
      <Footer />
    </>
  );
};

export default Home;
